import React, { useEffect } from "react";
import { range } from "lodash-es";

import ContainerButton from "../buttons/ContainerButton";
import Card from "../cards/Card";

import styles from "./OneCardOf.module.scss";

interface OneCardOfProps<T extends React.Key> {
  className?: string;
  selected: T;
  onChange?(selected: T): void;
  autoselect?(element: JSX.Element, i: number): boolean;
  children?: React.ReactNode;
  selectedColor?: string;
}

export default function OneCardOf<T extends React.Key>({
  className,
  selected,
  onChange,
  autoselect,
  children,
  selectedColor,
}: OneCardOfProps<T>) {
  // Must use [children].flat() to maintain keys
  const childArray = [children].flat();

  if (process.env.NODE_ENV === "development") {
    for (const child of [children].flat()) {
      if (!React.isValidElement(child)) continue;
      if (child.type !== Card) continue;
      if (child.key) continue;
      throw new Error("Card child of OneCardOf must have a defined key.");
    }
  }

  useEffect(() => {
    if (children)
      if (autoselect) {
        for (const i of range(childArray.length)) {
          const child = childArray[i];
          if (!React.isValidElement(child)) continue;
          if (child.type !== Card) continue;

          if (autoselect(child, i)) {
            onChange(child.key as T);
            return;
          }
        }
      }
  }, [!!autoselect, childArray.filter(Boolean).length > 0]);

  return (
    <div className={className}>
      {React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) return child;
        if (child.type !== Card) return child;
        return (
          <ContainerButton
            onClick={onChange && (() => onChange(child.key as T))}
          >
            <Card.PropsProvider className={styles.card}>
              {React.cloneElement<Card.Props>(child, {
                outline:
                  selected === child.key
                    ? selectedColor ?? "var(--primary)"
                    : true,
              })}
            </Card.PropsProvider>
          </ContainerButton>
        );
      })}
    </div>
  );
}
